import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Play, Pause, RotateCcw, CheckCircle2, Loader2, Circle, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ngn = (n) => `\u20A6${Math.round(n || 0).toLocaleString()}`;

/* ============================================================
   Damilola Coker — 13-scene deterministic pitch rehearsal.
   Every scene is a small, self-contained function that hits an
   already-shipped /api/demo/gdl/* endpoint. Total target ≈ 7 min.
   ============================================================ */

const SCENES = [
  {
    id: 1, chapter: 'Onboarding',
    title: 'Reset scenario & seed the cast',
    detail: 'Wipe demo tables, reseed Damilola Coker as verified federal officer (GL10, Ministry of Finance).',
    est_ms: 1400,
  },
  {
    id: 2, chapter: 'Identity',
    title: 'Verify Damilola against IPPIS registry',
    detail: 'Match employee_number 20170555 + name similarity + salary band. Auto-verified in <2s.',
    est_ms: 1200,
  },
  {
    id: 3, chapter: 'Salary Engine',
    title: 'January salary lands',
    detail: '+₦450,000 credited to Damilola. Loan mandate & structure jobs enqueue.',
    est_ms: 1800,
  },
  {
    id: 4, chapter: 'Cinematic',
    title: 'Salary loop timeline',
    detail: 'Adamu-style 8-second particle flow: credit → mandate → structure → notify → ATOM.',
    est_ms: 3800,
  },
  {
    id: 5, chapter: 'ATOM',
    title: 'ATOM suggests school-fees vault',
    detail: 'Post-salary suggestion generated. Fetch open suggestions for Damilola.',
    est_ms: 1000,
  },
  {
    id: 6, chapter: 'ATOM',
    title: 'Damilola accepts — ₦100,000 ring-fenced',
    detail: 'Vault-lock 30 days @ 14% APR. Balance rebalances spendable → ring-fenced.',
    est_ms: 1400,
  },
  {
    id: 7, chapter: 'Loans',
    title: 'Apply for ₦1,500,000 loan',
    detail: 'Origination draft opened. Employer verification, credit score & mandate scaffolding.',
    est_ms: 1500,
  },
  {
    id: 8, chapter: 'Loans',
    title: 'Offer accepted → disbursed',
    detail: 'AutoPay salary mandate authorised. Principal + tenure locked (33% APR, 12 mo).',
    est_ms: 1800,
  },
  {
    id: 9, chapter: 'Everyday',
    title: 'Airtime bill on MTN',
    detail: '₦2,000 debit. Commission accrues to platform.',
    est_ms: 1000,
  },
  {
    id: 10, chapter: 'Wealth',
    title: 'Invest ₦75,000 in MMF',
    detail: 'GDL Money Market Fund @ 15% APR. Yield accrues daily.',
    est_ms: 1200,
  },
  {
    id: 11, chapter: 'Merchant',
    title: 'Prestige Suits checkout ₦85,000',
    detail: 'Create order → simulate customer paid → webhook delivered → 1.5% commission booked.',
    est_ms: 2400,
  },
  {
    id: 12, chapter: 'Section E',
    title: 'PAYE restructure preview',
    detail: 'Convert 22% of ₦450k gross into food/transport/HMO benefits. State extrapolation reveals ₦31.2B/year.',
    est_ms: 1400,
  },
  {
    id: 13, chapter: 'Boardroom',
    title: 'Revenue summary + admin console',
    detail: 'Total monthly run-rate across lending, MMF spread, checkout commission, GDL Pay.',
    est_ms: 1200,
  },
];

export default function DemoDirector() {
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(0);          // 0 = idle, 1..13 = scene
  const [status, setStatus] = useState({});           // { sceneId: 'ok'|'err' }
  const [detail, setDetail] = useState({});           // { sceneId: string }
  const [elapsed, setElapsed] = useState(0);
  const [damilolaId, setDamilolaId] = useState(null);
  const startedAt = useRef(null);
  const abort = useRef(false);

  // Elapsed clock
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => {
      if (startedAt.current) setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
    }, 250);
    return () => clearInterval(iv);
  }, [running]);

  const reset = () => {
    abort.current = true;
    setRunning(false);
    setCurrent(0);
    setStatus({});
    setDetail({});
    setElapsed(0);
    startedAt.current = null;
  };

  const wait = (ms) => new Promise(r => setTimeout(r, ms));

  const run = async () => {
    abort.current = false;
    setRunning(true);
    setStatus({});
    setDetail({});
    setElapsed(0);
    startedAt.current = Date.now();

    let csId = null;
    const ctx = {};   // scene-to-scene state

    for (const scene of SCENES) {
      if (abort.current) break;
      setCurrent(scene.id);
      try {
        const info = await execScene(scene.id, csId, ctx);
        if (info?.civil_servant_id) csId = info.civil_servant_id;
        setDamilolaId(csId);
        setStatus(s => ({ ...s, [scene.id]: 'ok' }));
        setDetail(d => ({ ...d, [scene.id]: info?.detail || '✓ complete' }));
      } catch (e) {
        setStatus(s => ({ ...s, [scene.id]: 'err' }));
        setDetail(d => ({ ...d, [scene.id]: e?.response?.data?.detail || e.message || 'failed' }));
      }
      // Pacing: pause between scenes for animation breathing room
      await wait(Math.max(400, scene.est_ms * 0.35));
    }
    setRunning(false);
    setCurrent(0);
  };

  /* Individual scene runners */
  const execScene = async (id, csId, ctx = {}) => {
    switch (id) {
      case 1: {
        await axios.post(`${API}/api/demo/gdl/admin/reset`, {});
        // Poll until background rebuild finishes (usually 20-40s).
        const deadline = Date.now() + 90_000;
        while (Date.now() < deadline) {
          await wait(2000);
          const st = await axios.get(`${API}/api/demo/gdl/admin/reset-status`).then(r => r.data).catch(() => null);
          if (st?.damilola_ready) break;
        }
        return { detail: 'Cast reseeded · Damilola Coker verified · registry clean · hydrated' };
      }
      case 2: {
        const { data } = await axios.get(`${API}/api/demo/gdl/admin/civil-servants`);
        const dami = data.find(s => s.full_name === 'Damilola Coker');
        if (!dami) throw new Error('Damilola not found after seed');
        return {
          civil_servant_id: dami.id,
          detail: `Employee #20170555 · GL${dami.grade_level} · ${dami.state} · Verified`,
        };
      }
      case 3: {
        if (!csId) throw new Error('civil-servant id missing');
        const { data } = await axios.post(`${API}/api/demo/gdl/admin/simulate-salary/${csId}`, {});
        return {
          detail: `+${ngn(data?.salary_credited || 450000)} salary · loan deduction ${ngn(data?.loan_deduction || 0)} · balance ${ngn(data?.available_balance || 0)}`,
        };
      }
      case 4: {
        const { data } = await axios.get(`${API}/api/demo/gdl/timeline/salary/${csId}`);
        if (!data?.available) return { detail: 'No timeline data (skipped)' };
        return { detail: `${data.steps?.length || 0} particle steps · ends at ${ngn(data.final_balance)}` };
      }
      case 5: {
        const { data } = await axios.get(`${API}/api/demo/gdl/atom/suggestions/${csId}`);
        const s = (data || [])[0];
        return { detail: s ? `“${s.headline}”` : 'ATOM idle (no suggestion this loop)' };
      }
      case 6: {
        const r = await axios.post(`${API}/api/demo/gdl/atom/vault-lock/${csId}`, {
          amount: 100000, days: 30, rate: 0.14, label: 'School fees vault',
        });
        return { detail: `Ring-fenced ${ngn(r.data?.ring_fenced || 100000)} · unlocks in 30 days` };
      }
      case 7: {
        const r = await axios.post(`${API}/api/demo/gdl/loan/apply/${csId}`, { requested_amount: 1500000 });
        ctx.loanAppId = r.data?.id;
        return {
          detail: `Application ${r.data?.id?.slice(0,8) || 'draft'} · approved ${ngn(r.data?.approved_amount || 0)} · monthly ${ngn(r.data?.monthly_repayment || 0)}`,
        };
      }
      case 8: {
        if (!ctx.loanAppId) throw new Error('loan application id missing from scene 7');
        const r = await axios.post(`${API}/api/demo/gdl/loan/accept/${ctx.loanAppId}`, {});
        return {
          detail: `Loan ${r.data?.loan_id?.slice(0,8) || ''} disbursed · +${ngn(r.data?.amount || 0)} · new balance ${ngn(r.data?.balance || 0)}`,
        };
      }
      case 9: {
        await axios.post(`${API}/api/demo/gdl/bills/${csId}`, {
          category: 'Airtime', provider: 'MTN', identifier: '08012340000', amount: 2000,
        });
        return { detail: `MTN airtime · ${ngn(2000)} · commission earned` };
      }
      case 10: {
        const r = await axios.post(`${API}/api/demo/gdl/invest/${csId}`, {
          fund_name: 'GDL Money Market Fund', amount: 75000,
        });
        return { detail: `MMF @ 15% APR · principal ${ngn(75000)}` };
      }
      case 11: {
        // Onboard a merchant, create order, simulate paid
        const m = await axios.post(`${API}/api/demo/gdl/admin/merchants/onboard`, {
          legal_name: 'Prestige Suits Ltd', display_name: 'Prestige Suits',
          settlement_bank: 'GTBank', settlement_account: '0123456789',
          webhook_url: 'https://prestige.example/hook', brand_color: '#0A1F44',
        });
        const ord = await axios.post(`${API}/api/demo/gdl/checkout/orders`, {
          merchant_id: m.data.id, amount: 85000,
          customer_name: 'Damilola Coker', description: 'Bespoke suit deposit',
        });
        const paid = await axios.post(`${API}/api/demo/gdl/checkout/orders/${ord.data.id}/simulate-paid`, {});
        return { detail: `Order ${ngn(85000)} settled · commission ${ngn(paid.data?.platform_fee || 0)} · merchant net ${ngn(paid.data?.merchant_net || 0)}` };
      }
      case 12: {
        const r = await axios.post(`${API}/api/demo/gdl/admin/calculator/print-view`, {
          monthly_gross: 450000, state_headcount: 100000,
        });
        return { detail: r.data?.extrapolation_label || 'Section E preview generated' };
      }
      case 13: {
        const r = await axios.get(`${API}/api/demo/gdl/admin/revenue-summary`);
        return { detail: `Monthly run-rate ${ngn(r.data?.total_monthly_run_rate || 0)}` };
      }
      default:
        throw new Error('Unknown scene');
    }
  };

  const totalEst = Math.round(SCENES.reduce((a, s) => a + s.est_ms, 0) / 1000);
  const doneCount = Object.values(status).filter(v => v === 'ok').length;
  const errCount = Object.values(status).filter(v => v === 'err').length;

  return (
    <div data-testid="demo-director-panel" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Preamble card */}
      <div style={{
        background: 'linear-gradient(135deg, #0A1F44 0%, #12305F 55%, #1E4E8A 100%)',
        color: '#F5F1E8', borderRadius: 16, padding: '20px 22px', marginBottom: 16,
        boxShadow: '0 10px 30px rgba(10,31,68,0.25)', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -30, right: -30, width: 140, height: 140,
          borderRadius: '50%', border: '1px solid rgba(201,162,75,0.25)', pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative' }}>
          <div style={{
            display: 'inline-block', padding: '4px 10px', borderRadius: 999,
            background: 'rgba(201,162,75,0.18)', color: '#C9A24B',
            fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
          }}>Demo Director · Internal</div>
          <div style={{
            fontFamily: 'Playfair Display, Georgia, serif', fontSize: 22, fontWeight: 600,
            marginTop: 10, letterSpacing: '-0.01em',
          }}>
            13-scene rehearsal · Damilola Coker
          </div>
          <div style={{ fontSize: 13, color: 'rgba(245,241,232,0.65)', marginTop: 6, maxWidth: 640, lineHeight: 1.55 }}>
            One button drives the full pitch loop end-to-end: identity → salary → ATOM → loans → everyday spend → wealth → merchant → Section E → boardroom. Auto-drive completes in ~{Math.round(totalEst * 1.35)}s; a presenter can narrate over the top.
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={running ? null : run}
              disabled={running}
              data-testid="demo-director-run"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: running ? 'rgba(201,162,75,0.35)' : '#C9A24B',
                color: running ? 'rgba(245,241,232,0.6)' : '#0A1F44',
                border: 'none', padding: '11px 22px', borderRadius: 999,
                fontWeight: 700, fontSize: 14, cursor: running ? 'wait' : 'pointer',
                boxShadow: '0 4px 12px rgba(201,162,75,0.3)',
                transition: 'transform 0.15s ease',
              }}>
              {running ? <Loader2 size={16} style={{ animation: 'gdl-spin 1s linear infinite' }} /> : <Play size={16} />}
              {running ? `Scene ${current}/13 in flight…` : 'Run full auto-rehearsal'}
            </button>
            <button
              onClick={reset}
              data-testid="demo-director-reset"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'transparent', color: 'rgba(245,241,232,0.75)',
                border: '1px solid rgba(245,241,232,0.15)', padding: '10px 16px', borderRadius: 999,
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}>
              <RotateCcw size={14} /> Reset director
            </button>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 18, alignItems: 'center' }}>
              <Stat label="Elapsed" value={`${Math.floor(elapsed/60)}:${String(elapsed%60).padStart(2,'0')}`} />
              <Stat label="Done" value={`${doneCount}/13`} accent="#A7F3D0" />
              {errCount > 0 && <Stat label="Errors" value={errCount} accent="#FCA5A5" />}
            </div>
          </div>
        </div>
      </div>

      {/* Scene grid */}
      <div style={{ display: 'grid', gap: 10 }}>
        {SCENES.map(s => {
          const st = status[s.id];
          const isCurrent = current === s.id && running;
          const Icon = st === 'ok' ? CheckCircle2 : st === 'err' ? XCircle : isCurrent ? Loader2 : Circle;
          const iconColor =
            st === 'ok' ? '#2E7D32'
            : st === 'err' ? '#B93030'
            : isCurrent ? '#C9A24B'
            : '#8CA0BA';
          const bg = isCurrent
            ? 'linear-gradient(90deg, #FFFDF3 0%, #FFF8E1 100%)'
            : st === 'ok'
            ? '#F6FBF7'
            : st === 'err'
            ? '#FEF2F2'
            : '#FFFFFF';
          const borderColor = isCurrent ? '#C9A24B' : st === 'ok' ? '#BFE1C4' : st === 'err' ? '#F0BCBC' : '#E6E1D2';

          return (
            <div key={s.id} data-testid={`demo-scene-${s.id}`} style={{
              background: bg, border: `1px solid ${borderColor}`,
              borderRadius: 12, padding: '14px 16px',
              display: 'flex', gap: 14, alignItems: 'flex-start',
              transition: 'all 0.25s ease',
              transform: isCurrent ? 'translateX(4px)' : 'none',
              boxShadow: isCurrent ? '0 4px 16px rgba(201,162,75,0.2)' : 'none',
            }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: isCurrent ? 'rgba(201,162,75,0.15)' : 'rgba(10,31,68,0.04)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Icon size={18} color={iconColor}
                  style={isCurrent ? { animation: 'gdl-spin 1s linear infinite' } : {}} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: '#0A1F44',
                    background: 'rgba(10,31,68,0.06)', padding: '2px 8px', borderRadius: 999,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}>{s.chapter}</span>
                  <span style={{
                    fontSize: 15, fontWeight: 700, color: '#0A1F44',
                    fontFamily: 'Playfair Display, Georgia, serif',
                  }}>Scene {s.id}. {s.title}</span>
                </div>
                <div style={{ fontSize: 13, color: '#4A5A78', marginTop: 4, lineHeight: 1.5 }}>
                  {s.detail}
                </div>
                {detail[s.id] && (
                  <div data-testid={`demo-scene-${s.id}-detail`} style={{
                    marginTop: 8, fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    color: st === 'err' ? '#B93030' : '#2E7D32',
                    background: 'rgba(255,255,255,0.6)', padding: '6px 10px', borderRadius: 8,
                    borderLeft: `3px solid ${st === 'err' ? '#B93030' : '#2E7D32'}`,
                  }}>
                    {detail[s.id]}
                  </div>
                )}
              </div>
              <div style={{
                fontSize: 11, color: '#8CA0BA', fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                minWidth: 42, textAlign: 'right', flexShrink: 0,
              }}>
                ~{(s.est_ms/1000).toFixed(1)}s
              </div>
            </div>
          );
        })}
      </div>

      {damilolaId && (
        <div style={{ marginTop: 14, fontSize: 12, color: '#4A5A78' }}>
          <strong>Damilola civil_servant_id:</strong>{' '}
          <code style={{ background: '#F5F1E8', padding: '2px 6px', borderRadius: 4 }}>{damilolaId}</code>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = '#F5F1E8' }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 10, color: 'rgba(245,241,232,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{value}</div>
    </div>
  );
}
